import type { Film } from "@/mocks/films";

export type SortKey = "newest" | "oldest" | "rating" | "popular";

export type FilterState = {
  region: string;
  genre: string;
  year: string;
  rating: string;
};

export const yearOptions = ["全部", "2020后", "2010-2019", "2000-2009", "2000前"];

export const ratingOptions = ["全部", "8.5分以上", "8.0分以上", "7.5分以上"];

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最新上线" },
  { key: "oldest", label: "年份最早" },
  { key: "rating", label: "评分最高" },
  { key: "popular", label: "热度最高" },
];

export const emptyFilters: FilterState = {
  region: "全部",
  genre: "全部",
  year: "全部",
  rating: "全部",
};

export const matchYear = (year: number, option: string) => {
  if (option === "全部") return true;
  if (option === "2020后") return year >= 2020;
  if (option === "2010-2019") return year >= 2010 && year <= 2019;
  if (option === "2000-2009") return year >= 2000 && year <= 2009;
  if (option === "2000前") return year < 2000;
  return true;
};

export const matchRating = (rating: number, option: string) => {
  if (option === "全部") return true;
  if (option === "8.5分以上") return rating >= 8.5;
  if (option === "8.0分以上") return rating >= 8.0;
  if (option === "7.5分以上") return rating >= 7.5;
  return true;
};

export const filterFilms = (list: Film[], filters: FilterState): Film[] =>
  list.filter(
    (film) =>
      (filters.region === "全部" || film.region === filters.region) &&
      (filters.genre === "全部" || film.genres.includes(filters.genre)) &&
      matchYear(film.year, filters.year) &&
      matchRating(film.rating, filters.rating),
  );

export const sortFilms = (list: Film[], sort: SortKey): Film[] => {
  const copy = [...list];
  switch (sort) {
    case "oldest":
      return copy.sort((a, b) => a.year - b.year);
    case "rating":
      return copy.sort((a, b) => b.rating - a.rating);
    case "popular":
      return copy.sort((a, b) => b.popularity - a.popularity);
    default:
      return copy.sort((a, b) => b.year - a.year);
  }
};

export const searchFilms = (list: Film[], keyword: string): Film[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return list.filter(
    (film) =>
      film.title.toLowerCase().includes(q) ||
      film.originalTitle.toLowerCase().includes(q) ||
      film.director.toLowerCase().includes(q) ||
      film.genres.some((g) => g.includes(q)) ||
      film.tags.some((t) => t.toLowerCase().includes(q)) ||
      film.cast.some((member) => member.name.toLowerCase().includes(q)) ||
      film.cast.some((member) => member.role.toLowerCase().includes(q)),
  );
};

export const recommendFilms = (target: Film, list: Film[], limit = 4): Film[] => {
  const scored = list
    .filter((film) => film.id !== target.id)
    .map((film) => {
      const genreScore = film.genres.filter((g) => target.genres.includes(g)).length * 3;
      const tagScore = film.tags.filter((t) => target.tags.includes(t)).length * 2;
      const regionScore = film.region === target.region ? 1 : 0;
      return { film, score: genreScore + tagScore + regionScore };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.film.rating - a.film.rating);
  return scored.slice(0, limit).map((item) => item.film);
};

export const paginate = <T,>(list: T[], page: number, pageSize: number) => {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    currentPage: safePage,
  };
};